import { inject, Injectable } from '@angular/core';
import { injectOrderApi } from './order-api';
import {
  OrderBookNumberResponse,
  OrderReportShippingDelayRequest,
  OrderReportShippingDelayResponse,
  OrderRestartRequest,
  OrderRestartResponse,
  OrderValidateFilesRequest,
  OrderValidateFilesResponse
} from './order-api.models';
import { OrderDbService } from './order-db.service';
import { Order } from './order.models';

@Injectable({ providedIn: 'root' })
export class OrderService {
  private orderApi = injectOrderApi();
  private orderDbService = inject(OrderDbService);

  /**
   * Finds an order by customer email and order number.
   * @param email Customer email associated to the order.
   * @param orderNumber Public order number.
   * @returns Promise with `{ id, data }` shape (kept for backward compatibility) or `null` if not found.
   */
  async getOrderByEmailAndNumber(email: string, orderNumber: number): Promise<{ id: string; data: Order; } | null> {
    const order = await this.orderDbService.getOneWhere({
      where: { email, number: orderNumber },
      limit: 1
    });
    if (!order) return null;
    const { id, ...data } = order;
    return { id, data: data as Order };
  }

  /**
   * Total number of orders for a user (server-side count, no document download).
   * @param userId User ID to filter the orders by.
   * @returns Promise with the total number of orders for the given user ID.
   */
  countOrdersByUserId(userId: string): Promise<number> {
    return this.orderDbService.countDocs({ where: { userId } });
  }

  // -------------------------------------------------------------------------
  // API wrappers (Firebase Functions)
  // -------------------------------------------------------------------------

  /**
   * Reserves and returns a new order number assigned by the server.
   * @returns Promise with the booked order number.
   * @description Note that this does not create an order, it only reserves a
   * number for future use. The client is expected to create the order document later with the assigned number.
   */
  getOrderBookNumber(): Promise<OrderBookNumberResponse> {
    return this.orderApi.bookNumber();
  }

  /**
   * Validates that the files of the cart items are available.
   * @param request Cart items payload to validate.
   */
  validateOrderFiles(request: OrderValidateFilesRequest): Promise<OrderValidateFilesResponse> {
    return this.orderApi.validateFiles(request);
  }

  /**
   * Restarts an order (resets it to its initial state) by id.
   * @param request Restart payload with the target order id.
   */
  restartOrder(request: OrderRestartRequest): Promise<OrderRestartResponse> {
    return this.orderApi.restart(request);
  }

  /**
   * Notifies the server about a shipping delay for the given order.
   * @param request Report payload with the affected order.
   */
  reportOrderShippingDelay(request: OrderReportShippingDelayRequest): Promise<OrderReportShippingDelayResponse> {
    return this.orderApi.reportShippingDelay(request);
  }
}
